import React, { useState } from "react";
import { Alert, Button, Form, Modal, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonLoading, FormInput } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { ReactComponent as InfoCircleSVG } from "go-component/images/svg/info-circle.svg";
import { ReactComponent as RemoveSVG } from "go-core/images/svg/actions/remove.svg";
import { FormDirty } from "go-form/components/FormDirty";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { FILTER_VALUE_SEPARATOR } from "go-list/core/components/Filter/services";
import { apiOrganization } from "../../../../../../../../../../../services/Api/Organization/apiOrganization";
import {
	DeviceApi,
	DirectionApi,
	DirectionApiForm,
	DirectionApiFormPayload,
	OtherDirectionsApi,
} from "../../../../../../../../../../../services/Api/Organization/types";

interface Props {
	direction: DirectionApi;
	onHide: () => void;
	handleSave: (direction: DirectionApi) => void;
}

const DirectionModalForm = (props: Props): JSX.Element => {
	const form = useForm<DirectionApiForm>({
		criteriaMode: "all",
		defaultValues: props.direction,
	});
	const { t } = useTranslation();
	const {
		register,
		handleSubmit,
		formState: { errors },
		setError,
		formState,
		control,
		watch,
	} = form;
	const [loading, setLoading] = useState(false);
	const { addFlash, addSuccessFlash } = useFlash();

	const { fields, remove, append } = useFieldArray({
		control,
		name: "devices",
		keyName: "key",
	});

	const {
		fields: directionsFields,
		remove: removeDirection,
		append: appendDirection,
	} = useFieldArray({
		control,
		name: "other_directions",
		keyName: "key",
	});

	const onSubmit = handleSubmit(async (data: DirectionApiForm) => {
		setLoading(true);
		const newData: DirectionApiFormPayload = {
			...data,
			id: props.direction.id,
			devices: (data.devices ?? [])
				.filter((device): device is DeviceApi => Boolean(device?.id))
				.map((device) => device.id),
			other_directions: (data.other_directions ?? [])
				.filter((otherDirection): otherDirection is OtherDirectionsApi => Boolean(otherDirection?.id))
				.map((otherDirection) => otherDirection.id),
		};
		const params: Record<string, any> = { include: "devices" };

		try {
			let res;
			if (newData.id) {
				res = await apiOrganization.updateDirection(newData, params);
			} else {
				res = await apiOrganization.createDirection(newData, params);
			}
			props.handleSave(res);
			addSuccessFlash(t("common.flash.saved", { ns: "lib" }));
		} catch (e) {
			handleError.form(e, setError, addFlash);
		}

		setLoading(false);
	});

	const searchDevices = (search: string, params: Record<string, any>, options?: Record<string, any>) => {
		return apiOrganization.getDevicesSearchSelect(
			search,
			{ ...params, "type|e": "PRINTER", "connection_interface|e": "LAN" },
			{
				cancelToken: options?.token,
			}
		);
	};

	const searchOtherDirections = async (
		search: string,
		params: Record<string, any>,
		options?: Record<string, any>
	) => {
		const newParams: Record<string, any> = { ...params };
		const alreadySelectedOtherDirections = watch("other_directions");
		let directionsFilterValue: string = "";

		if (props.direction.id) {
			directionsFilterValue += props.direction.id;
		}
		if (alreadySelectedOtherDirections) {
			directionsFilterValue += alreadySelectedOtherDirections.reduce<string>((newFilterValue, watchedOption) => {
				if (watchedOption?.id) {
					return `${newFilterValue}${FILTER_VALUE_SEPARATOR}${watchedOption.id}`;
				}

				return newFilterValue;
			}, "");
		}

		if (directionsFilterValue) {
			newParams["id|ne"] = directionsFilterValue;
		}

		return apiOrganization.getDirectionsSearchSelect(search, newParams, {
			cancelToken: options?.token,
		});
	};

	return (
		<Modal show onHide={props.onHide}>
			<Modal.Header closeButton>
				<Modal.Title>
					{props.direction.id
						? `${t("common.word.directions")} "${props.direction.name}"`
						: t("modules.direction.header.new.title")}
				</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<FormDirty formState={formState} key="direction-role-form" noValidate onSubmit={onSubmit}>
					<FormInput
						label={t("common.word.name", { ns: "lib" })}
						register={register}
						name="name"
						errors={errors}
					/>
				</FormDirty>
				<Form.Group className={"form-group mb-0"}>
					<Form.Label className="mb-0">{t("modules.direction.field.devices.title")}</Form.Label>
				</Form.Group>
				<Alert variant="warning" className="mt-2">
					{t("modules.direction.constraints.direction_only_with_lan")}
				</Alert>
				<table className="table table-form w-100">
					<thead>
						<tr>
							<th className="action" />
							<th />
							<th className="action" />
						</tr>
					</thead>
					<tbody>
						{fields.map((item, index) => {
							return (
								<tr key={fields[index].key}>
									<td className="action" />
									<td>
										<FormSelectGroup
											name={`devices.${index}.id`}
											getOptionLabel={(option) => option.label}
											getOptionValue={(option) => option.id}
											placeholder={t("modules.direction.action.choose_device.title")}
											errors={errors}
											defaultValue={
												item.name
													? {
															label: item?.name,
															id: item?.id,
													  }
													: undefined
											}
											loadOptions={searchDevices}
											control={control}
											data-testid={`devices.${index}.id`}
										/>
									</td>
									<td className="action">
										<RemoveSVG className="icon" onClick={() => remove(index)} />
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
				<Button variant="add" type="button" onClick={() => append({ name: "" } as DeviceApi)}>
					+ {t("common.action.add", { ns: "lib" })}
				</Button>
				<Form.Group className="form-group mt-3">
					<Form.Label className="mb-0 d-flex align-items-center column-gap-2">
						{t("common.word.other_directions")}
						<OverlayTrigger
							overlay={
								<Tooltip>
									<span>{t("modules.direction.field.other_direction_tooltip_message.title")}</span>
								</Tooltip>
							}
						>
							<InfoCircleSVG />
						</OverlayTrigger>
					</Form.Label>
				</Form.Group>
				<table className="table table-form w-100">
					<thead>
						<tr>
							<th className="action" />
							<th />
							<th className="action" />
						</tr>
					</thead>
					<tbody>
						{directionsFields.map((directionField, index) => {
							return (
								<tr key={directionField.key}>
									<td className="action" />
									<td>
										<FormSelectGroup
											name={`other_directions.${index}.id`}
											placeholder={t("modules.direction.action.choose_other_direction.title")}
											getOptionValue={(option) => option.id}
											errors={errors}
											loadOptions={searchOtherDirections}
											control={control}
											data-testid={`other_directions.${index}.id`}
											defaultValue={{
												label: directionField.name,
												id: directionField.id,
											}}
										/>
									</td>
									<td className="action">
										<RemoveSVG className="icon" onClick={() => removeDirection(index)} />
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
				<Button variant="add" onClick={() => appendDirection({ name: "" })}>
					+ {t("common.action.add", { ns: "lib" })}
				</Button>
			</Modal.Body>
			<Modal.Footer>
				<ButtonLoading loading={loading} variant="primary" type="submit" onClick={onSubmit}>
					{t("common.action.save", { ns: "lib" })}
				</ButtonLoading>
				<Button variant="light" onClick={props.onHide}>
					{t("common.action.cancel", { ns: "lib" })}
				</Button>
			</Modal.Footer>
		</Modal>
	);
};
export default DirectionModalForm;
